import { getPageDataWithBbox, getPageImage } from "./Api";

type Entry<T> = { promise: Promise<T>; createdAt: number };
type Cache<T> = Map<string, Entry<T>>;

// Backend signed URLs expire in 10–15 min. Keep cache entries comfortably
// under that so we never serve a stale URL, and so partial page payloads
// (fetched while a page is still being processed) get re-fetched soon.
const TTL_MS = 5 * 60 * 1000;

const imageCache: Cache<any> = new Map();
const dataCache: Cache<any> = new Map();
let activeDocumentId: string | null = null;

const key = (documentID: string, pageId: string) => `${documentID}:${pageId}`;

const ensureScope = (documentID: string) => {
  if (activeDocumentId !== documentID) {
    imageCache.clear();
    dataCache.clear();
    activeDocumentId = documentID;
  }
};

const sharedFetch = <T>(
  cache: Cache<T>,
  k: string,
  fetcher: () => Promise<T>,
): Promise<T> => {
  const existing = cache.get(k);
  if (existing && Date.now() - existing.createdAt < TTL_MS) {
    return existing.promise;
  }
  const entry: Entry<T> = { promise: undefined as unknown as Promise<T>, createdAt: Date.now() };
  entry.promise = fetcher().catch((err) => {
    // Don't cache rejections, and don't clobber a newer entry that may
    // have replaced ours while this request was in flight.
    if (cache.get(k) === entry) cache.delete(k);
    throw err;
  });
  cache.set(k, entry);
  return entry.promise;
};

/**
 * Returns a shared promise for the page image response. Left and right
 * panels both call this for the same page; only one network round-trip
 * is issued.
 */
export const getCachedPageImage = (documentID: string, pageId: string) => {
  ensureScope(documentID);
  return sharedFetch(imageCache, key(documentID, pageId), () =>
    getPageImage(documentID, pageId),
  );
};

/**
 * Returns a shared promise for the page data (with bboxes) response.
 * The bbox variant is a superset of the non-bbox response, so the right
 * panel can read `readable_data` from it without an extra request.
 */
export const getCachedPageDataWithBbox = (
  documentID: string,
  pageId: string,
) => {
  ensureScope(documentID);
  return sharedFetch(dataCache, key(documentID, pageId), () =>
    getPageDataWithBbox(documentID, pageId),
  );
};

/** Drop any cached data/image entry for a page so the next call re-fetches. */
export const invalidatePageCache = (documentID: string, pageId: string) => {
  const k = key(documentID, pageId);
  dataCache.delete(k);
  imageCache.delete(k);
};

